import { HashMap } from '@zerops/zef/core';
import mergeWith from 'lodash-es/mergeWith';

/**
 * Generic WebSocket subscription name generator for time-series modules
 */
export function createTimeSeriesSubscriptionName(
  featureName: string,
  groupBy: string,
  timeGroupBy: string,
  key?: string,
  projectId?: string,
  serviceId?: string,
  metricId?: string
): string {
  const base = `${featureName}_${groupBy.includes('transaction') ? 'transaction-group' : 'resource-consumption-group'}_${groupBy}_${timeGroupBy}_##${key}##`;

  if (projectId) { return `${base}-${projectId}`; }
  if (serviceId) { return `${base}-${serviceId}`; }
  if (metricId) { return `${base}-${metricId}`; }

  return base;
}

/**
 * Generic existence check for grouped time-series data
 */
export function timeSeriesGroupExists<TPayload>(
  payload: TPayload,
  stateData: HashMap<any>,
  getRangeKey: (key: string) => string,
  getGroupBy: (payload: TPayload) => string,
  getGroupRange: (payload: TPayload) => { key: string },
  getProjectId?: (payload: TPayload) => string | undefined,
  getServiceId?: (payload: TPayload) => string | undefined
): boolean {
  const groupBy = getGroupBy(payload);
  const groupRange = getGroupRange(payload);
  const rangeKey = getRangeKey(groupRange.key);
  const projectId = getProjectId?.(payload);
  const serviceId = getServiceId?.(payload);

  // if group doesn't exist at all
  if (stateData[groupBy] === undefined) {
    return false;
  }

  // if group is project level (flat structure)
  if ((groupBy === 'projectId' || groupBy.includes('Project')) && 
      stateData[groupBy][rangeKey] !== undefined) {
    return true;
  }

  // if we are checking single project existence inside a group
  if (!!projectId && 
      (groupBy === 'stackId' || groupBy.includes('Service')) &&
      stateData[groupBy][projectId] &&
      stateData[groupBy][projectId][rangeKey] !== undefined) {
    return true;
  }

  // if we are checking single service existence inside a group
  if (!!serviceId && 
      (groupBy === 'metricId' || groupBy.includes('Metric')) &&
      stateData[groupBy][serviceId] &&
      stateData[groupBy][serviceId][rangeKey] !== undefined) {
    return true;
  }

  return false;
}

/**
 * Generic data transformation for time-series grouped data
 */
export function transformTimeSeriesGroupData(
  data: {
    items: any[];
    groupBy: string;
    key?: string;
  },
  state: HashMap<any>,
  getRangeKey: (key: string) => string,
  customTransforms?: {
    [groupBy: string]: (items: any[], rangeKey: string) => any;
  }
): HashMap<any> {
  // Handle undefined or null data
  if (!data) {
    return state;
  }

  const { items, groupBy, key } = data;

  // Use custom transform if available
  if (customTransforms && customTransforms[groupBy]) {
    const rangeKey = getRangeKey(key);
    const transformedData = customTransforms[groupBy](items, rangeKey);
    
    return {
      ...state,
      [groupBy]: mergeWith(
        { ...state[groupBy] },
        { ...transformedData },
        (oldVal, newVal) => {
          if (newVal) {
            return { ...oldVal, ...newVal };
          }
          return oldVal;
        }
      )
    };
  }

  // Default flat structure (for project-level grouping)
  if (groupBy === 'projectId' || groupBy.includes('Project')) {
    const rangeKey = getRangeKey(key);
    
    return {
      ...state,
      [groupBy]: {
        ...(state[groupBy] || {}),
        [rangeKey]: {
          data: items?.length ? items : (state?.[groupBy]?.[rangeKey]?.data || [])
        }
      }
    };
  }

  return state;
}

/**
 * Generic subscription item tracking with deduplication
 */
export function trackTimeSeriesSubscriptionItem<T>(
  trackedItems: T[],
  newItem: T,
  getSubscriptionName: (item: T) => string
): T[] {
  const subscriptionName = getSubscriptionName(newItem);
  
  return trackedItems.some(item => getSubscriptionName(item) === subscriptionName)
    ? trackedItems
    : [...trackedItems, newItem];
}

